import { useCallback } from 'react';
import { useToast } from './use-toast';
import { writeExcel, type WriteSheetData } from '@/lib/excelEngine';
import { useAuth } from '@/contexts/AuthContext';
import { canExport } from '@/lib/demoGuard';

/**
 * Hook for exporting empreendimentos data to Excel (XLSX) and PDF (print).
 * Uses excelEngine abstraction (ExcelJS) — no direct xlsx dependency.
 * Blocked for demo users (UI layer of defense; backend also enforces).
 */
export function useEmpExport() {
  const { toast } = useToast();
  const { isDemo } = useAuth();

  const blockedForDemo = useCallback(() => {
    if (!canExport({ isDemo })) {
      toast({
        title: 'Exportação desabilitada',
        description: 'Exportação de dados não está disponível no modo demonstração.',
        variant: 'destructive',
      });
      return true;
    }
    return false;
  }, [isDemo, toast]);

  const exportToExcel = useCallback(async (
    data: Record<string, unknown>[],
    filename: string,
    sheetName = 'Dados'
  ) => {
    if (blockedForDemo()) return;
    if (!data.length) {
      toast({ title: 'Sem dados para exportar', variant: 'destructive' });
      return;
    }

    try {
      await writeExcel([{ name: sheetName, data }], filename);
      toast({ title: 'Exportação concluída', description: `${filename}.xlsx baixado.` });
    } catch (err) {
      console.error('Export error:', err);
      toast({ title: 'Erro na exportação', description: String(err), variant: 'destructive' });
    }
  }, [toast, blockedForDemo]);

  const exportMultiSheet = useCallback(async (
    sheets: Array<{ name: string; data: Record<string, unknown>[] }>,
    filename: string
  ) => {
    if (blockedForDemo()) return;
    const nonEmpty = sheets.filter(s => s.data.length > 0);
    if (!nonEmpty.length) {
      toast({ title: 'Sem dados para exportar', variant: 'destructive' });
      return;
    }

    try {
      await writeExcel(nonEmpty, filename);
      toast({ title: 'Exportação concluída', description: `${filename}.xlsx com ${nonEmpty.length} abas.` });
    } catch (err) {
      console.error('Export error:', err);
      toast({ title: 'Erro na exportação', description: String(err), variant: 'destructive' });
    }
  }, [toast, blockedForDemo]);

  const exportToPDF = useCallback((title: string) => {
    if (blockedForDemo()) return;
    const originalTitle = document.title;
    document.title = title;
    window.print();
    document.title = originalTitle;
  }, [blockedForDemo]);

  return { exportToExcel, exportMultiSheet, exportToPDF };
}
